import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdLink, MdLinkOff, MdRefresh, MdSync, MdFolder } from "react-icons/md";
import { RecencyChip } from "src/components/RecencyChip/RecencyChip";
import { usePraxisSoftwareSync, type SyncLogEntry, type FolderConnectionState } from "src/context/PraxisSoftwareSyncContext";
import type { FolderType } from "src/services/gdtSyncStorage";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";

/** A self-contained status tile, not a thin label-left/button-right row — two of these
 *  sit in a responsive grid (see .pvs-sync-folder-grid) so the pair genuinely fills
 *  whatever width the parent gives them instead of one column of rows stretching into
 *  a big empty gap between a short label and a far-right button. Works in both the wide
 *  standalone page and the narrower Settings sub-section: minmax(240px, 1fr) collapses
 *  to one column when the container is too narrow for two. */
function FolderTile({
    type,
    label,
    state,
    folderName,
    onConnect,
    onReconnect,
    onDisconnect,
}: {
    type: FolderType;
    label: string;
    state: FolderConnectionState;
    folderName: string | null;
    onConnect: () => void;
    onReconnect: () => void;
    onDisconnect: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div className={`pvs-sync-tile pvs-sync-tile--${type}`}>
            <div className="pvs-sync-tile__header">
                <span className="pvs-sync-tile__label">
                    <span className={`pvs-sync-dot pvs-sync-dot--${state === "connected" ? "on" : state === "needs-reconnect" ? "warn" : "off"}`} />
                    {label}
                </span>
                {state === "connected" && (
                    <button
                        className="pvs-sync-tile__disconnect"
                        onClick={onDisconnect}
                        aria-label={`${t("praxisSoftwareSync.disconnectBtn")} – ${label}`}
                    >
                        {/* Icon alone was read as "broken link"/status, not as an action — so it's labelled. */}
                        <MdLinkOff aria-hidden="true" />
                        <span>{t("praxisSoftwareSync.disconnectBtn")}</span>
                    </button>
                )}
            </div>

            <span className="pvs-sync-tile__status">
                {state === "checking" && <span className="pvs-sync-tile__spinner" />}
                {state === "connected"       ? t("praxisSoftwareSync.connected") :
                 state === "needs-reconnect" ? t("praxisSoftwareSync.needsReconnect") :
                 state === "checking"        ? t("praxisSoftwareSync.checkingConnection") :
                 t("praxisSoftwareSync.notConnected")}
            </span>

            {/* Browsers never expose the full OS path for a picked directory, only its own
                name — that's the most specific "where" a File System Access API app can
                show, not a limitation of this display. */}
            {folderName && (
                <span className="pvs-sync-tile__folder" title={folderName}>
                    <MdFolder /> {folderName}
                </span>
            )}

            {state !== "connected" && state !== "checking" && (
                <button
                    className="pvs-sync-tile__cta"
                    onClick={state === "needs-reconnect" ? onReconnect : onConnect}
                    aria-label={`${state === "needs-reconnect" ? t("praxisSoftwareSync.reconnectBtn") : t("praxisSoftwareSync.connectBtn")} – ${label}`}
                >
                    {state === "needs-reconnect"
                        ? <><MdRefresh /> {t("praxisSoftwareSync.reconnectBtn")}</>
                        : <><MdLink /> {t("praxisSoftwareSync.connectBtn")}</>}
                </button>
            )}
        </div>
    );
}

function LogRow({ entry }: { entry: SyncLogEntry }) {
    return (
        <div className={`pvs-sync-log-entry pvs-sync-log-entry--${entry.status}`}>
            <span className="pvs-sync-log-entry__time">{new Date(entry.timestamp).toLocaleTimeString("de-DE")}</span>
            <span className="pvs-sync-log-entry__file">{entry.filename}</span>
            <span className="pvs-sync-log-entry__message">{entry.message}</span>
        </div>
    );
}

/** Connect/disconnect controls, auto-parse toggle, sync-now button, and live progress —
 *  everything except the log. Split out from the log so a full page can lay them out
 *  differently (compact controls card + a wide table below) while Settings keeps both
 *  stacked in one section (see PraxisSoftwareSyncBody below). */
export function PraxisSoftwareSyncControls() {
    const { t } = useTranslation();
    const {
        supported, connection, folderNames, connectFolder, reconnectFolder, disconnectFolder,
        syncing, syncNow, lastSyncAt, autoParse,
    } = usePraxisSoftwareSync();

    if (!supported) {
        return <p className="pvs-sync-desc">{t("praxisSoftwareSync.unsupportedBrowser")}</p>;
    }

    const anyConnected = connection.gdt === "connected" || connection.dat === "connected";

    const [pendingDisconnect, setPendingDisconnect] = useState<{ type: FolderType; label: string } | null>(null);
    const confirmDisconnect = (type: FolderType, label: string) => {
        setPendingDisconnect({ type, label });
    };

    return (
        <>
            {pendingDisconnect && (
                <ConfirmPopup
                    message={t("praxisSoftwareSync.confirmDisconnect", { folder: pendingDisconnect.label })}
                    onConfirm={() => {
                        void disconnectFolder(pendingDisconnect.type);
                        setPendingDisconnect(null);
                    }}
                    onCancel={() => setPendingDisconnect(null)}
                />
            )}
            <div className="pvs-sync-folder-grid">
                <FolderTile
                    type="gdt"
                    label={t("praxisSoftwareSync.gdtFolder")}
                    state={connection.gdt}
                    folderName={folderNames.gdt}
                    onConnect={() => void connectFolder("gdt")}
                    onReconnect={() => void reconnectFolder("gdt")}
                    onDisconnect={() => confirmDisconnect("gdt", t("praxisSoftwareSync.gdtFolder"))}
                />
                <FolderTile
                    type="dat"
                    label={t("praxisSoftwareSync.datFolder")}
                    state={connection.dat}
                    folderName={folderNames.dat}
                    onConnect={() => void connectFolder("dat")}
                    onReconnect={() => void reconnectFolder("dat")}
                    onDisconnect={() => confirmDisconnect("dat", t("praxisSoftwareSync.datFolder"))}
                />
            </div>

            {/* Deliberately narrower than the folder grid above — a button+timestamp doesn't have
                enough content to fill a wide container without the same empty-gap problem the
                folder grid exists to avoid. */}
            <div className="pvs-sync-controls-narrow">
                {/* Auto-processing is now a praxis-wide setting (Settings → Praxis Software Sync),
                    not a per-browser toggle — this just says what's currently in effect. */}
                <p className="pvs-sync-autoparse-note">
                    {autoParse ? t("praxisSoftwareSync.autoParseOnNote") : t("praxisSoftwareSync.autoParseOffNote")}
                </p>

                <div className="pvs-sync-row">
                    <div className="pvs-sync-row__main">
                        <button
                            className="pvs-sync-action-btn pvs-sync-action-btn--primary"
                            onClick={() => void syncNow()}
                            disabled={!anyConnected || syncing}
                        >
                            <MdSync /> {syncing ? t("praxisSoftwareSync.syncing") : t("praxisSoftwareSync.syncNow")}
                        </button>
                        {lastSyncAt && (
                            <span className="pvs-sync-row__sync-time">
                                {t("praxisSoftwareSync.lastSync")}: <RecencyChip timestamp={lastSyncAt} />{" "}
                                {new Date(lastSyncAt).toLocaleString("de-DE")}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

/** Compact log — a simple scrollable list, sized for the narrower Settings sub-section.
 *  The full-page destination (PraxisSoftwareSyncPage.tsx) uses its own proper <table>
 *  instead (PraxisSoftwareSyncLogTable), matching the rest of the app's data tables. */
export function PraxisSoftwareSyncLogList() {
    const { t } = useTranslation();
    const { supported, syncLog, clearSyncLog } = usePraxisSoftwareSync();
    if (!supported || syncLog.length === 0) return null;

    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const handleClear = () => {
        setShowClearConfirm(false);
        void clearSyncLog();
    };

    return (
        <>
            {showClearConfirm && (
                <ConfirmPopup
                    message={t("praxisSoftwareSync.confirmClearLog")}
                    onConfirm={handleClear}
                    onCancel={() => setShowClearConfirm(false)}
                />
            )}
            <div className="pvs-sync-log-header">
                <button type="button" className="pvs-sync-clear-btn" onClick={() => setShowClearConfirm(true)}>
                    {t("praxisSoftwareSync.clearLog")}
                </button>
            </div>
            <div className="pvs-sync-log">
                {syncLog.map((entry) => <LogRow key={entry.id} entry={entry} />)}
            </div>
        </>
    );
}

